import * as React from "react";

/**
 * SPEC-DOCCONSENT-3b — shared inline markdown renderer.
 *
 * Originally lived inside LegalDocumentDialog (SPEC-DOCCONSENT-2b);
 * extracted here so DisclaimerGate can reuse it without duplicating the
 * parser. Authored markdown supported:
 *   `# `         → <h2>
 *   `## `        → <h3>
 *   `- `         → grouped <ul><li>
 *   `---`        → <hr/>
 *   blank line   → paragraph break (visual spacing comes from `mt-*`)
 *   `**bold**`   → inline <strong> (split on `**`, odd indices are bold)
 *
 * Unrecognised lines fall through as plain paragraphs, so new markdown
 * features render as text instead of crashing.
 */
export function renderMarkdown(source: string): React.ReactNode[] {
  const lines = source.split(/\r?\n/);
  const nodes: React.ReactNode[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    nodes.push(
      <ul key={`ul-${key++}`} className="my-2 list-disc space-y-1 pl-5">
        {items.map((item, i) => (
          <li key={i} className="text-sm leading-relaxed text-muted-foreground">
            {renderInline(item)}
          </li>
        ))}
      </ul>,
    );
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");

    if (line.startsWith("- ")) {
      bullets.push(line.slice(2));
      continue;
    }
    flushBullets();

    if (line.trim() === "") {
      continue;
    }
    if (line.trim() === "---") {
      nodes.push(<hr key={`hr-${key++}`} className="my-4 border-border" />);
      continue;
    }
    if (line.startsWith("## ")) {
      nodes.push(
        <h3
          key={`h3-${key++}`}
          className="mt-4 mb-1 text-base font-semibold text-foreground"
        >
          {renderInline(line.slice(3))}
        </h3>,
      );
      continue;
    }
    if (line.startsWith("# ")) {
      nodes.push(
        <h2
          key={`h2-${key++}`}
          className="mt-2 mb-2 text-lg font-semibold text-foreground"
        >
          {renderInline(line.slice(2))}
        </h2>,
      );
      continue;
    }
    nodes.push(
      <p
        key={`p-${key++}`}
        className="mt-2 text-sm leading-relaxed text-muted-foreground"
      >
        {renderInline(line)}
      </p>,
    );
  }
  flushBullets();
  return nodes;
}

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/\*\*/);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-foreground">
        {part}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}
